import { Star } from "lucide-react";
import { getInitials } from "@/utils/initials";

interface TestimonialCardProps {
  quote: string;
  source: string;
  role?: string;
  year?: string;
  context?: string;
  onClick?: () => void;
}

export function TestimonialCard({
  quote,
  source,
  role,
  year,
  context,
  onClick
}: TestimonialCardProps) {
  const initials = getInitials(source);

  return (
    <div
      className="group bg-slate-900/70 border border-emerald-300/20 rounded-2xl p-6 shadow-[0_18px_55px_rgba(15,23,42,0.9)] hover:-translate-y-1 hover:shadow-lg transition-all duration-200 ease-out cursor-pointer"
      onClick={onClick}
    >
      {/* Header: Avatar + Name + Badge */}
      <div className="flex items-start gap-3 mb-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500 text-sm font-semibold text-white flex-shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-emerald-100 text-sm truncate">
            {source}
          </p>
          {role && (
            <span className="inline-flex items-center rounded-full bg-emerald-900/40 px-2.5 py-0.5 text-xs font-medium text-emerald-200 mt-1">
              {role}
            </span>
          )}
        </div>
      </div>

      {/* Star Rating */}
      <div className="flex gap-1 text-emerald-400 mb-3">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="h-4 w-4 fill-current" />
        ))}
      </div>

      {/* Quote */}
      <p className="text-sm text-slate-200/90 leading-relaxed mb-4 italic line-clamp-4">
        "{quote}"
      </p>

      {/* Footer: Year + Context OR Read full findings link for MBA study */}
      <div className="flex items-center justify-between text-xs text-slate-400">
        {source.includes("MBA NHS frontline study") ? (
          <span className="text-emerald-400 flex items-center gap-1 font-medium">
            Read full findings <span className="text-base">↓</span>
          </span>
        ) : (
          <>
            {year && <span>{year}</span>}
            {context && <span className="text-slate-500">• {context}</span>}
          </>
        )}
      </div>
    </div>
  );
}
